import React from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import type {
  GetNotificationsFilters,
  KnownNotificationEvent,
  NotificationEntity,
  NotificationType,
  SystemNotificationPolicyMatrixResponse,
  UpdateNotificationSettingsDto,
  UpdateSystemNotificationPoliciesDto,
} from "@/types/notifications";
import { notificationsService } from "@/services/notificationsService";
import type { MarkReadDto, MarkSingleReadDto } from "@/types/notifications";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { isNotificationVisible } from "@/lib/notifications";

/**
 * Stable serialization for filters:
 * - removes undefined/null
 * - sorts keys
 */
function normalizeFilters(filters: GetNotificationsFilters) {
  const cleaned: Record<string, any> = {};

  Object.entries(filters ?? {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    cleaned[k] = v;
  });

  return Object.keys(cleaned)
    .sort()
    .reduce(
      (acc, k) => {
        acc[k] = cleaned[k];
        return acc;
      },
      {} as Record<string, any>,
    );
}

function filterNotificationsByCapabilities(
  items: NotificationEntity[],
  canUseRequestsSystem: boolean,
) {
  return items.filter((item) =>
    isNotificationVisible(item, canUseRequestsSystem),
  );
}

export const notificationsQueryKeys = {
  all: ["notifications"] as const,

  lists: () => [...notificationsQueryKeys.all, "list"] as const,
  list: (filters: GetNotificationsFilters) =>
    [...notificationsQueryKeys.lists(), normalizeFilters(filters)] as const,

  unreadCount: () => [...notificationsQueryKeys.all, "unread-count"] as const,
};

export const notificationSettingsQueryKeys = {
  all: ["notification-settings"] as const,
  current: () => [...notificationSettingsQueryKeys.all, "current"] as const,
  system: () => [...notificationSettingsQueryKeys.all, "system"] as const,
  systemCurrent: (event: KnownNotificationEvent) =>
    [...notificationSettingsQueryKeys.system(), "current", event] as const,
};

export function useNotifications(
  filters: GetNotificationsFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: notificationsQueryKeys.list(filters),
    queryFn: () => notificationsService.getNotifications(filters),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
    gcTime: 5 * 60_000,
    enabled,
  });
}

type UseInfiniteNotificationsArgs = {
  enabled: boolean;
  hotelId?: string;
  isRead?: boolean; // unread tab uses false; all tab uses undefined
  limit?: number;
};

export function useInfiniteNotifications(args: UseInfiniteNotificationsArgs) {
  const { enabled, hotelId, isRead, limit = 20 } = args;
  const { capabilities } = useAccessCapabilities();

  const baseFilters = React.useMemo(
    () => ({
      ...(hotelId ? { hotelId } : {}),
      ...(isRead !== undefined ? { isRead } : {}),
      limit,
    }),
    [hotelId, isRead, limit],
  );

  const query = useInfiniteQuery({
    queryKey: [
      ...notificationsQueryKeys.lists(),
      normalizeFilters(baseFilters),
    ],
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      notificationsService.getNotifications({
        ...baseFilters,
        page: pageParam,
        limit,
      } as any),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (acc, p) => acc + (p.data?.length ?? 0),
        0,
      );
      const total = lastPage.total ?? 0;
      if (loaded >= total) return undefined;
      return allPages.length + 1; // next page index
    },
    staleTime: 10_000,
    gcTime: 5 * 60_000,
  });

  const items = React.useMemo(() => {
    const pages = query.data?.pages ?? [];
    const map = new Map<string, NotificationEntity>();
    for (const p of pages) {
      for (const n of p.data ?? []) map.set(n.id, n);
    }
    return filterNotificationsByCapabilities(
      Array.from(map.values()),
      capabilities.canUseRequestsSystem,
    );
  }, [capabilities.canUseRequestsSystem, query.data]);

  const total = query.data?.pages?.[0]?.total ?? 0;

  return {
    items,
    total,
    hasMore: query.hasNextPage,
    isInitialLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (!query.isFetchingNextPage && query.hasNextPage) query.fetchNextPage();
    },
    reset: () => query.refetch(), // або qc.removeQueries за потреби
  };
}

export function useUnreadNotificationsCount({
  enabled = true,
  hotelId,
  type,
}: {
  enabled?: boolean;
  hotelId?: string;
  type?: NotificationType;
} = {}) {
  const { capabilities } = useAccessCapabilities();

  return useQuery({
    queryKey: [
      ...notificationsQueryKeys.unreadCount(),
      hotelId ?? null,
      type ?? null,
      capabilities.canUseRequestsSystem,
    ],
    queryFn: async () => {
      if (type === "TASK" && !capabilities.canUseRequestsSystem) {
        return { count: 0 };
      }

      if (capabilities.canUseRequestsSystem) {
        return notificationsService.getUnreadCount({
          ...(hotelId ? { hotelId } : {}),
          ...(type ? { type } : {}),
        });
      }

      const limit = 100;
      let page = 1;
      let loaded = 0;
      let total = 0;
      const notifications: NotificationEntity[] = [];

      do {
        const response = await notificationsService.getNotifications({
          ...(hotelId ? { hotelId } : {}),
          ...(type ? { type } : {}),
          isRead: false,
          page,
          limit,
        });

        notifications.push(...response.data);
        loaded += response.data.length;
        total = response.total ?? notifications.length;
        page += 1;
      } while (loaded < total);

      return {
        count: filterNotificationsByCapabilities(
          notifications,
          capabilities.canUseRequestsSystem,
        ).length,
      };
    },
    staleTime: 10_000,
    gcTime: 5 * 60_000,
    enabled,
  });
}

// -----------------------
// Mutations (optional but usually needed)
// -----------------------

export function useMarkNotificationRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: MarkSingleReadDto }) =>
      notificationsService.markAsRead(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsQueryKeys.lists() });
      qc.invalidateQueries({ queryKey: notificationsQueryKeys.unreadCount() });
    },
  });
}

export function useMarkNotificationsBulkRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: MarkReadDto) => notificationsService.markBulkAsRead(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsQueryKeys.lists() });
      qc.invalidateQueries({ queryKey: notificationsQueryKeys.unreadCount() });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsQueryKeys.lists() });
      qc.invalidateQueries({ queryKey: notificationsQueryKeys.unreadCount() });
    },
  });
}

export function useNotificationSettings(enabled = true) {
  return useQuery({
    queryKey: notificationSettingsQueryKeys.current(),
    queryFn: () => notificationsService.getSettings(),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    enabled,
  });
}

export function useUpdateNotificationSettings() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateNotificationSettingsDto) =>
      notificationsService.updateSettings(dto),

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: notificationSettingsQueryKeys.current(),
      });
    },
  });
}

export function useSystemNotificationSettings(enabled = true) {
  return useQuery({
    queryKey: notificationSettingsQueryKeys.system(),
    queryFn: () => notificationsService.getSystemSettings(),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    enabled,
  });
}

export function useCurrentSystemNotificationSetting(
  event: KnownNotificationEvent,
  enabled = true,
) {
  return useQuery({
    queryKey: notificationSettingsQueryKeys.systemCurrent(event),
    queryFn: () => notificationsService.getCurrentSystemSetting(event),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    enabled,
  });
}

export function useUpdateSystemNotificationSettings() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateSystemNotificationPoliciesDto) =>
      notificationsService.updateSystemSettings(dto),
    onSuccess: (data: SystemNotificationPolicyMatrixResponse) => {
      qc.setQueryData(notificationSettingsQueryKeys.system(), data);
      qc.invalidateQueries({
        queryKey: notificationSettingsQueryKeys.system(),
      });
    },
  });
}
